import type {
  EmbeddedAuthErrorMessage,
  EmbeddedAuthErrorPayload,
  EmbeddedAuthHandshakeAckMessage,
  EmbeddedAuthHandshakeAckPayload,
  EmbeddedAuthIframeMessage,
  EmbeddedAuthRequestPayload,
  EmbeddedAuthStateMessage,
  EmbeddedAuthStatePayload,
  EmbeddedIframeMessage,
} from '@/types/embeddedApplication';

function hasBaseAuthPayload(
  payload: unknown,
): payload is EmbeddedAuthRequestPayload {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const candidate = payload as Partial<EmbeddedAuthRequestPayload>;

  return (
    candidate.version === 1 &&
    typeof candidate.correlationId === 'string' &&
    candidate.correlationId.length > 0 &&
    typeof candidate.productId === 'string' &&
    candidate.productId.length > 0
  );
}

export class AuthMessageBridge {
  isEmbeddedAuthMessage(
    message: EmbeddedIframeMessage,
  ): message is EmbeddedAuthIframeMessage {
    if (
      message.type !== 'embeddedAuthRequired' &&
      message.type !== 'embeddedAuthRefreshRequired' &&
      message.type !== 'embeddedAuthLogout'
    ) {
      return false;
    }

    return hasBaseAuthPayload(message.payload);
  }

  toAuthRequestPayload(
    message: EmbeddedAuthIframeMessage,
  ): EmbeddedAuthRequestPayload {
    const requestedScopes =
      'requestedScopes' in message.payload
        ? message.payload.requestedScopes
        : undefined;
    const tenantHint =
      'tenantHint' in message.payload ? message.payload.tenantHint : undefined;

    return {
      version: 1,
      correlationId: message.payload.correlationId,
      productId: message.payload.productId,
      requestedScopes,
      tenantHint,
    };
  }

  toHandshakeAckMessage(
    payload: EmbeddedAuthHandshakeAckPayload,
  ): EmbeddedAuthHandshakeAckMessage {
    return {
      type: 'embeddedAuthHandshakeAck',
      payload,
    };
  }

  toAuthStateMessage(
    payload: EmbeddedAuthStatePayload,
  ): EmbeddedAuthStateMessage {
    return {
      type: 'embeddedAuthState',
      payload,
    };
  }

  toAuthErrorMessage(
    payload: EmbeddedAuthErrorPayload,
  ): EmbeddedAuthErrorMessage {
    return {
      type: 'embeddedAuthError',
      payload,
    };
  }
}

export const authMessageBridge = new AuthMessageBridge();
